import { ChangeEvent, useState } from 'react';
import { useLang } from 'hooks/lang';

import { actions, isOwner } from 'entities';

import { LinearProgress, Card } from 'components/Blocks';

import { UploadIcon } from 'components/Icons';

import { useUser } from 'hooks/user';
import { useProjectProfile } from '../../context';

import { fileNameToReadable } from 'utils/text';

export function NewProjectFile() {
  const [uploadProgress, setUploadProgress] = useState(0);

  const user = useUser();
  const { project, updateProject } = useProjectProfile();

  const { t } = useLang();

  const isProjectOwner = isOwner(project, user);

  const addFile = actions.projects.addFileToProject.use({
    onError: () => setUploadProgress(0),

    onSuccess: (newFile) =>
      updateProject((old) => ({
        ...old,
        files: [...old.files, newFile],
      })),
  });

  const executeUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const [target] = event.target.files || [];

    if (!target) return;

    const success = await addFile.execute({
      project: project.id,
      file: target,
      name: fileNameToReadable(target.name),
      onUploadProgress: ({ loaded, total }) =>
        setUploadProgress((loaded / (total || 100000000)) * 100),
    });

    if (!success) return;
  };

  if (!isProjectOwner) return null;

  if (addFile.loading)
    return (
      <Card className="border-primary">
        <LinearProgress value={uploadProgress} className="w-full max-w-32" />
      </Card>
    );

  return (
    <>
      {isProjectOwner && (
        <label
          className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed p-3 transition-colors hover:bg-hover"
          htmlFor="input-file"
        >
          <UploadIcon color="primary" />

          {t('upload-a-file-here')}

          <input
            multiple={false}
            name="input-file"
            id="input-file"
            type="file"
            className="hidden"
            onChange={executeUpload}
          />
        </label>
      )}
    </>
  );
}
